import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import refoulementService, { MotifRefoulement, RefoulementData, RefoulementResponse } from '@/services/refoulementService';
import RefoulementTicket from '../RefoulementTicket';
import { generateRefoulementPDF, printPDF, downloadPDF, emailPDF } from '@/services/pdfService';

interface RefoulementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: RefoulementData) => Promise<void>;
  peseeId: number;
  peseeReference?: string;
  peseeData?: {
    reference: string;
    grossWeight: number;
    tareWeight: number;
    netWeight: number;
    vehicle: string;
    driver: string;
    product: string;
    site: string;
    tenant: string;
    grossAt: string;
    tareAt: string;
    grossUser: string;
    tareUser: string;
  };
  isLoading?: boolean;
}

const RefoulementModal: React.FC<RefoulementModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  peseeId,
  peseeReference,
  peseeData,
  isLoading = false
}) => {
  const [motifs, setMotifs] = useState<MotifRefoulement[]>([]);
  const [motifsGroupes, setMotifsGroupes] = useState<Record<string, MotifRefoulement[]>>({});
  const [selectedMotifs, setSelectedMotifs] = useState<string[]>([]);
  const [observations, setObservations] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMotifs, setFilteredMotifs] = useState<MotifRefoulement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [showTicket, setShowTicket] = useState(false);
  const [refoulementResponse, setRefoulementResponse] = useState<RefoulementResponse | null>(null);

  // Charger les motifs au montage du composant
  useEffect(() => {
    if (isOpen) {
      loadMotifs();
    }
  }, [isOpen]);

  // Filtrer les motifs selon le terme de recherche
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = motifs.filter(motif => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (motif.nom && motif.nom.toLowerCase().includes(searchLower)) ||
          (motif.description && motif.description.toLowerCase().includes(searchLower)) ||
          (motif.categorie && motif.categorie.toLowerCase().includes(searchLower))
        );
      });
      setFilteredMotifs(filtered);
    } else {
      setFilteredMotifs(motifs);
    }
  }, [searchTerm, motifs]);

  const loadMotifs = async () => {
    try {
      setLoading(true);
      setError('');
      const motifsData = await refoulementService.getMotifsRefoulement();
      
      // Debug: Afficher la structure des données reçues
      console.log('🔍 Données reçues de l\'API:', motifsData);
      console.log('🔍 Premier motif:', motifsData[0]);
      
      // Vérifier et nettoyer les données
      const cleanedMotifs = motifsData.map((motif, index) => {
        console.log(`🔍 Motif ${index}:`, motif);
        return {
          ...motif,
          nom: motif.libelle || `Motif ${index + 1}`,
          description: motif.description || 'Description non disponible',
          categorie: motif.categorie || 'Autres',
          criticite: motif.criticite || 'Non défini'
        };
      });
      
      console.log('🔍 Motifs nettoyés:', cleanedMotifs);
      setMotifs(cleanedMotifs);
      
      // Grouper les motifs par catégorie
      const groupes: Record<string, MotifRefoulement[]> = {};
      cleanedMotifs.forEach(motif => {
        const categorie = motif.categorie;
        if (!groupes[categorie]) {
          groupes[categorie] = [];
        }
        groupes[categorie].push(motif);
      });
      setMotifsGroupes(groupes);
    } catch (err) {
      console.error('Erreur lors du chargement des motifs:', err);
      setError('Erreur lors du chargement des motifs de refoulement');
    } finally {
      setLoading(false);
    }
  };

  const handleMotifToggle = (motifId: string) => {
    setSelectedMotifs(prev => {
      if (prev.includes(motifId)) {
        return prev.filter(id => id !== motifId);
      } else {
        return [...prev, motifId];
      }
    });
    setValidationErrors([]);
  };

  const handleSelectAllInCategory = (categorie: string) => {
    const motifsInCategory = motifsGroupes[categorie] || [];
    const motifIds = motifsInCategory.map(motif => motif.id);
    
    setSelectedMotifs(prev => {
      const allSelected = motifIds.every(id => prev.includes(id));
      if (allSelected) {
        // Désélectionner tous les motifs de cette catégorie
        return prev.filter(id => !motifIds.includes(id));
      } else {
        // Sélectionner tous les motifs de cette catégorie
        const newSelection = [...prev];
        motifIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      }
    });
  };

  const handleClearAll = () => {
    setSelectedMotifs([]);
    setObservations('');
    setSearchTerm('');
    setValidationErrors([]);
  };

  const toggleCategory = (categorie: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categorie]: !prev[categorie]
    }));
  };

  const handleConfirm = async () => {
    // Validation
    const errors: string[] = [];
    
    if (selectedMotifs.length === 0) {
      errors.push('Au moins un motif doit être sélectionné');
    }
    
    if (observations.length > 1000) {
      errors.push('Les observations ne peuvent pas dépasser 1000 caractères');
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      const refoulementData: RefoulementData = {
        pesee_id: peseeId,
        motifs: selectedMotifs,
        observations: observations.trim()
      };

      // Créer le refoulement via l'API
      const response = await refoulementService.creerRefoulement(refoulementData);
      
      // Sauvegarder la réponse pour le ticket
      setRefoulementResponse(response);
      
      // Afficher le ticket
      setShowTicket(true);
      
      // Appeler la callback parent
      await onConfirm(refoulementData);
      
    } catch (err) {
      console.error('Erreur lors de la confirmation:', err);
      setError('Erreur lors de la création du refoulement');
    }
  };


  const getMotifsGroupesToDisplay = () => {
    if (searchTerm.trim()) {
      // Grouper les motifs filtrés
      const groupes: Record<string, MotifRefoulement[]> = {};
      filteredMotifs.forEach(motif => {
        const categorie = motif.categorie || 'Autres';
        if (!groupes[categorie]) {
          groupes[categorie] = [];
        }
        groupes[categorie].push(motif);
      });
      return groupes;
    }
    return motifsGroupes;
  };

  const getCriticiteColor = (criticite: string | undefined) => {
    if (!criticite) return 'text-gray-600 bg-gray-100';
    
    switch (criticite.toLowerCase()) {
      case 'critique': return 'text-red-600 bg-red-100';
      case 'haute': return 'text-orange-600 bg-orange-100';
      case 'moyenne': return 'text-yellow-600 bg-yellow-100';
      case 'faible': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handlePrintTicket = () => {
    if (!refoulementResponse || !peseeData) return;
    
    // Les motifs sont déjà des objets complets
    const motifsDetails = refoulementResponse.motifs.map(motif => ({
      libelle: motif.libelle || motif.nom || 'Motif inconnu',
      description: motif.description || 'Description non disponible',
      categorie: motif.categorie || 'Non définie',
      criticite: motif.criticite || 'Non définie',
      code: motif.code
    }));
    
    const pdfData = {
      numero_refoulement: refoulementResponse.numero_refoulement,
      date_refoulement: refoulementResponse.date_refoulement,
      pesee_reference: refoulementResponse.pesee_reference,
      agent_refoulement: refoulementResponse.agent_refoulement_nom || refoulementResponse.agent_refoulement_username || 'N/A',
      motifs: motifsDetails,
      observations: refoulementResponse.observations,
      peseeData: peseeData
    };
    
    const doc = generateRefoulementPDF(pdfData);
    printPDF(doc);
  };

  const handleExportTicket = () => {
    if (!refoulementResponse || !peseeData) return;
    
    // Les motifs sont déjà des objets complets
    const motifsDetails = refoulementResponse.motifs.map(motif => ({
      libelle: motif.libelle || motif.nom || 'Motif inconnu',
      description: motif.description || 'Description non disponible',
      categorie: motif.categorie || 'Non définie',
      criticite: motif.criticite || 'Non définie',
      code: motif.code
    }));
    
    const pdfData = {
      numero_refoulement: refoulementResponse.numero_refoulement,
      date_refoulement: refoulementResponse.date_refoulement,
      pesee_reference: refoulementResponse.pesee_reference,
      agent_refoulement: refoulementResponse.agent_refoulement_nom || refoulementResponse.agent_refoulement_username || 'N/A',
      motifs: motifsDetails,
      observations: refoulementResponse.observations,
      peseeData: peseeData
    };
    
    const doc = generateRefoulementPDF(pdfData);
    const filename = `refoulement_${refoulementResponse.numero_refoulement}.pdf`;
    downloadPDF(doc, filename);
  };

  const handleEmailTicket = () => {
    if (!refoulementResponse || !peseeData) return;
    
    // Les motifs sont déjà des objets complets
    const motifsDetails = refoulementResponse.motifs.map(motif => ({
      libelle: motif.libelle || motif.nom || 'Motif inconnu',
      description: motif.description || 'Description non disponible',
      categorie: motif.categorie || 'Non définie',
      criticite: motif.criticite || 'Non définie',
      code: motif.code
    }));
    
    const pdfData = {
      numero_refoulement: refoulementResponse.numero_refoulement,
      date_refoulement: refoulementResponse.date_refoulement,
      pesee_reference: refoulementResponse.pesee_reference,
      agent_refoulement: refoulementResponse.agent_refoulement_nom || refoulementResponse.agent_refoulement_username || 'N/A',
      motifs: motifsDetails,
      observations: refoulementResponse.observations,
      peseeData: peseeData
    };
    
    const doc = generateRefoulementPDF(pdfData);
    const filename = `refoulement_${refoulementResponse.numero_refoulement}.pdf`;
    emailPDF(doc, filename);
  };

  const handleCloseTicket = () => {
    setShowTicket(false);
    setRefoulementResponse(null);
    handleClearAll();
    onClose();
  };

  if (!isOpen) return null;

  // Afficher le ticket de refoulement si disponible
  if (showTicket && refoulementResponse && peseeData) {
    return (
      <RefoulementTicket
        refoulementData={refoulementResponse}
        peseeData={peseeData}
        onClose={handleCloseTicket}
        onPrint={handlePrintTicket}
        onExport={handleExportTicket}
        onEmail={handleEmailTicket}
      />
    );
  }

  // Gestion d'erreur pour éviter les crashes
  try {
    return (
      <div className="fixed inset-0 z-[10000] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Refoulement de pesée
                </h3>
                <p className="text-sm text-gray-600">
                  Référence: {peseeReference || `#${peseeId}`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-sm font-medium text-red-800 mb-2">Erreurs de validation:</h4>
                <ul className="text-sm text-red-600 list-disc list-inside">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recherche */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher un motif
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Tapez pour rechercher un motif..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Motifs sélectionnés */}
            {selectedMotifs.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    Motifs sélectionnés ({selectedMotifs.length})
                  </h4>
                  <button
                    onClick={handleClearAll}
                    className="text-sm text-red-600 hover:text-red-800 flex items-center"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Tout effacer
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedMotifs.map((motifId: string) => {
                    const motif = motifs.find((m: MotifRefoulement) => m.id === motifId);
                    return motif ? (
                      <span
                        key={motifId}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                      >
                        {motif.nom}
                        <button
                          onClick={() => handleMotifToggle(motifId)}
                          className="ml-2 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    ) : (
                      <span
                        key={motifId}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600"
                      >
                        Motif ID: {motifId}
                        <button
                          onClick={() => handleMotifToggle(motifId)}
                          className="ml-2 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Liste des motifs avec accordéons */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-4">
                Sélectionner les motifs de refoulement
              </h4>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600">Chargement des motifs...</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(getMotifsGroupesToDisplay()).map(([categorie, motifsInCategory]) => {
                    const isExpanded = expandedCategories[categorie] || false;
                    const selectedInCategory = motifsInCategory.filter(motif => selectedMotifs.includes(motif.id)).length;
                    const totalInCategory = motifsInCategory.length;
                    
                    return (
                      <div key={categorie} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* En-tête de l'accordéon */}
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => toggleCategory(categorie)}
                              className="flex items-center flex-1 text-left hover:bg-gray-100 p-2 -m-2 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                              {isExpanded ? (
                                <ChevronDownIcon className="h-5 w-5 text-gray-500 mr-2" />
                              ) : (
                                <ChevronRightIcon className="h-5 w-5 text-gray-500 mr-2" />
                              )}
                              <h5 className="font-medium text-gray-900">
                                {categorie}
                                {selectedInCategory > 0 && (
                                  <span className="ml-2 text-sm text-red-600 font-normal">
                                    ({selectedInCategory}/{totalInCategory} sélectionnés)
                                  </span>
                                )}
                              </h5>
                            </button>
                            <div className="flex items-center space-x-2 ml-4">
                              {selectedInCategory > 0 && (
                                <span className="text-xs text-gray-500">
                                  {selectedInCategory === totalInCategory ? 'Tous sélectionnés' : `${selectedInCategory} sur ${totalInCategory}`}
                                </span>
                              )}
                              <button
                                onClick={() => handleSelectAllInCategory(categorie)}
                                className="text-sm text-red-600 hover:text-red-800 flex items-center px-2 py-1 rounded hover:bg-red-50 transition-colors"
                              >
                                <PlusIcon className="h-4 w-4 mr-1" />
                                {selectedInCategory === totalInCategory ? 'Tout désélectionner' : 'Tout sélectionner'}
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Contenu de l'accordéon */}
                        {isExpanded && (
                          <div className="p-4 space-y-3 bg-white">
                            {motifsInCategory.map(motif => (
                              <label
                                key={motif.id}
                                className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedMotifs.includes(motif.id)}
                                  onChange={() => handleMotifToggle(motif.id)}
                                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-gray-900">
                                      {motif.nom || 'Nom non disponible'}
                                    </p>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCriticiteColor(motif.criticite)}`}>
                                      {motif.criticite || 'Non défini'}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {motif.description || 'Description non disponible'}
                                  </p>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Observations */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observations détaillées
                <span className="text-gray-500 font-normal ml-1">
                  (optionnel - {observations.length}/1000 caractères)
                </span>
              </label>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows={4}
                placeholder="Décrivez en détail les raisons du refoulement..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                maxLength={1000}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading || selectedMotifs.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Confirmation...
                </>
              ) : (
                <>
                  <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                  Confirmer le refoulement
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    );
  } catch (error) {
    console.error('Erreur dans RefoulementModal:', error);
    return (
      <div className="fixed inset-0 z-[10000] overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Erreur dans le modal
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Une erreur s'est produite lors du chargement du modal de refoulement.
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default RefoulementModal;
